/**
 * Experience Performance — section-grouped question distributions
 * for a single STANDARD Mystery Shopping survey.
 *
 * Reuses `FiltersBar` (date range + branch chip + branch picker +
 * competitor brand) and the same `DashboardLayout`. Backend endpoint:
 * GET /api/reports/experience-performance?survey_id=<id>&...
 *
 * Access is gated by the same MS product envelope as /reports — no
 * new feature flag.
 */
import React, { useEffect, useMemo, useState } from 'react';
import DashboardLayout from '../components/DashboardLayout';
import FiltersBar, { presetRange } from '../components/mystery_shopping/FiltersBar';
import { safeHtml } from '../lib/sanitize';
import { ChevronDown, ChevronUp, Search } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;


function EmptyState({ title, hint }) {
  return (
    <div
      data-testid="ep-empty-state"
      className="bg-white border border-dashed border-[#CBD5E1] rounded-xl p-10 text-center"
    >
      <p className="text-sm font-semibold text-[#0F172A]">{title}</p>
      {hint && <p className="text-xs text-[#64748B] mt-1">{hint}</p>}
    </div>
  );
}


// Branch-level breakdown, rendered inline when the parent card is
// expanded. Fetches on demand (lazy) and reuses the parent's query
// string so filters + branch scope match the overall card totals.
function BranchBreakdown({ questionId, options, queryString }) {
  const [state, setState] = useState({ loading: false, error: null, branches: [], loaded: false });
  const [search, setSearch] = useState('');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setState((s) => ({ ...s, loading: true, error: null }));
      try {
        const sep = queryString.includes('?') ? '&' : '?';
        const url = `${API}/experience-performance/question-branches${queryString}${sep}question_id=${encodeURIComponent(questionId)}`;
        const res = await fetch(url, { credentials: 'include' });
        if (!res.ok) {
          const err = await res.json().catch(() => ({}));
          if (!cancelled) setState({ loading: false, error: err.detail || `HTTP ${res.status}`, branches: [], loaded: true });
          return;
        }
        const body = await res.json();
        if (cancelled) return;
        setState({ loading: false, error: null, branches: body.branches || [], loaded: true });
      } catch (e) {
        if (!cancelled) setState({ loading: false, error: 'Failed to load branch breakdown', branches: [], loaded: true });
      }
    })();
    return () => { cancelled = true; };
    // Refetch when the parent filters change while expanded.
  }, [questionId, queryString]);

  const { loading, error, branches } = state;
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return branches;
    return branches.filter((b) => (b.branch_name || '').toLowerCase().includes(q));
  }, [branches, search]);
  const showSearch = branches.length > 10;

  if (loading) {
    return <div className="mt-3 text-xs text-[#64748B]" data-testid={`ep-branch-loading-${questionId}`}>Loading branches…</div>;
  }
  if (error) {
    return (
      <div className="mt-3 text-xs text-[#EF4444]" data-testid={`ep-branch-error-${questionId}`}>{error}</div>
    );
  }
  if (!branches.length) {
    return (
      <div
        className="mt-3 border border-dashed border-[#CBD5E1] rounded-md px-3 py-4 text-center text-xs text-[#64748B]"
        data-testid={`ep-branch-empty-${questionId}`}
      >
        No branch data available for this question with the current filters.
      </div>
    );
  }

  return (
    <div className="mt-3" data-testid={`ep-branch-breakdown-${questionId}`}>
      {showSearch && (
        <div className="mb-2 relative">
          <Search className="w-3.5 h-3.5 absolute left-2 top-1/2 -translate-y-1/2 text-[#94A3B8]" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={`Search ${branches.length} branches…`}
            className="w-full h-8 rounded-md border border-[#E2E8F0] bg-white pl-7 pr-3 text-xs text-[#0F172A] focus:outline-none focus:ring-2 focus:ring-[#1A3E6F]/20"
            data-testid={`ep-branch-search-${questionId}`}
          />
        </div>
      )}
      <div className="overflow-x-auto rounded-md border border-[#E2E8F0]">
        <table className="w-full text-xs">
          <thead>
            <tr className="bg-[#F8FAFC] text-[#475569]">
              <th className="text-left font-semibold px-3 py-2">Branch</th>
              <th className="text-center font-semibold px-3 py-2 w-[60px]">Base</th>
              {options.map((o) => (
                <th key={o.value} className="text-right font-semibold px-3 py-2 min-w-[80px]">
                  {o.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {filtered.map((b) => (
              <tr
                key={b.branch_id}
                className="border-t border-[#E2E8F0] hover:bg-[#F8FAFC]"
                data-testid={`ep-branch-row-${questionId}-${b.branch_id}`}
              >
                <td className="px-3 py-2 text-[#0F172A]">{b.branch_name}</td>
                <td className="px-3 py-2 text-center font-mono text-[#475569]">{b.base}</td>
                {options.map((o) => {
                  const cell = b.distribution && b.distribution[o.value];
                  const pct = cell && typeof cell.pct === 'number' ? cell.pct : null;
                  return (
                    <td
                      key={o.value}
                      className="px-3 py-2 text-right font-mono text-[#0F172A]"
                      data-testid={`ep-branch-cell-${questionId}-${b.branch_id}-${o.value}`}
                    >
                      {pct == null ? '—' : `${pct.toFixed(1)}%`}
                    </td>
                  );
                })}
              </tr>
            ))}
            {filtered.length === 0 && (
              <tr>
                <td
                  colSpan={2 + options.length}
                  className="px-3 py-3 text-center text-[#64748B]"
                  data-testid={`ep-branch-search-empty-${questionId}`}
                >
                  No branches match “{search}”.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}


// One card per question. Header = full cleaned question wording.
// Body = Base pill followed by every configured answer option and its
// percentage. Compact + responsive; long option lists scroll horizontally
// inside the card rather than wrapping the whole page.
function QuestionCard({ question, options, queryString }) {
  const { question_id, text, base, distribution, is_multi } = question;
  const [expanded, setExpanded] = useState(false);
  return (
    <div
      className="bg-white rounded-lg border border-[#E2E8F0] p-4"
      data-testid={`ep-card-${question_id}`}
    >
      <div
        className="text-sm font-semibold text-[#0F172A] leading-snug ep-question-text"
        data-testid={`ep-card-text-${question_id}`}
        // Question text is author-authored HTML; already sanitised on
        // save + on read via `safeHtml`. Only <p><br><b><i><u><ul><ol><li>
        // survive — see lib/sanitize.js.
        dangerouslySetInnerHTML={safeHtml(text)}
      />
      {is_multi && (
        <span
          className="mt-1 inline-block text-[10px] font-semibold uppercase tracking-wide text-[#64748B]"
          data-testid={`ep-multi-badge-${question_id}`}
        >
          Multi-select · totals may exceed 100 %
        </span>
      )}
      <div className="mt-3 overflow-x-auto">
        <div className="flex items-stretch gap-2 min-w-max">
          <div
            className="flex flex-col items-center justify-center rounded-md bg-[#F1F5F9] px-3 py-2 min-w-[60px]"
            data-testid={`ep-card-base-${question_id}`}
          >
            <span className="text-[10px] uppercase tracking-wider text-[#64748B] font-semibold">Base</span>
            <span className="text-sm font-mono font-semibold text-[#0F172A] mt-0.5">{base}</span>
          </div>
          {options.map((o) => {
            const cell = distribution && distribution[o.value];
            const pct = cell && typeof cell.pct === 'number' ? cell.pct : null;
            const empty = base === 0 || pct == null;
            return (
              <div
                key={o.value}
                className="flex flex-col items-center justify-center rounded-md bg-[#F8FAFC] border border-[#E2E8F0] px-3 py-2 min-w-[80px]"
                data-testid={`ep-card-opt-${question_id}-${o.value}`}
              >
                <span
                  className="text-[10px] text-[#475569] font-medium text-center leading-tight max-w-[110px] truncate"
                  title={o.label}
                >
                  {o.label}
                </span>
                <span className="text-sm font-mono font-semibold text-[#1A3E6F] mt-0.5">
                  {empty ? '—' : `${pct.toFixed(1)}%`}
                </span>
              </div>
            );
          })}
        </div>
      </div>

      {/* View by branch — lazy-loaded; hidden until the author asks for
          it, so we don't hammer the endpoint for every card on render. */}
      <div className="mt-3 pt-3 border-t border-[#E2E8F0]">
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          className="flex items-center gap-1.5 text-xs font-semibold text-[#1A3E6F] hover:text-[#0F172A] focus:outline-none focus:ring-2 focus:ring-[#1A3E6F]/20 rounded"
          data-testid={`ep-card-branch-toggle-${question_id}`}
          aria-expanded={expanded}
        >
          {expanded ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
          {expanded ? 'Hide branches' : 'View by branch'}
        </button>
        {expanded && (
          <BranchBreakdown
            questionId={question_id}
            options={options}
            queryString={queryString}
          />
        )}
      </div>
    </div>
  );
}


export default function ExperiencePerformance() {
  const [range, setRange] = useState(() => presetRange('all_time'));
  const [branchType, setBranchType] = useState('all');
  const [selectedBranchIds, setSelectedBranchIds] = useState([]);
  const [branches, setBranches] = useState([]);
  const [competitorBrand, setCompetitorBrand] = useState('');

  const [surveys, setSurveys] = useState([]);
  const [surveyId, setSurveyId] = useState('');
  const [surveysLoading, setSurveysLoading] = useState(true);
  const [surveysError, setSurveysError] = useState(null);

  const [data, setData] = useState(null);
  const [dataLoading, setDataLoading] = useState(false);
  const [dataError, setDataError] = useState(null);

  // ── Load selectable standard MS surveys once. ──
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setSurveysLoading(true);
      try {
        const res = await fetch(`${API}/experience-performance/surveys`, { credentials: 'include' });
        if (!res.ok) {
          const err = await res.json().catch(() => ({}));
          if (!cancelled) setSurveysError(err.detail || `HTTP ${res.status}`);
          return;
        }
        const body = await res.json();
        if (cancelled) return;
        setSurveys(body.surveys || []);
        // Auto-select the first survey so the user sees a table immediately.
        if ((body.surveys || []).length && !surveyId) {
          setSurveyId(body.surveys[0].survey_id);
        }
      } catch (e) {
        if (!cancelled) setSurveysError('Failed to load surveys');
      } finally {
        if (!cancelled) setSurveysLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // ── Load branch picker so FiltersBar can render its selector. Reuses
  //    the same /reports/branches endpoint used by the MS Dashboard. ──
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${API}/reports/branches`, { credentials: 'include' });
        if (!res.ok) return;
        const body = await res.json();
        if (!cancelled) setBranches(body.branches || []);
      } catch { /* non-fatal */ }
    })();
    return () => { cancelled = true; };
  }, []);

  // ── Fetch Experience Performance whenever a filter changes. ──
  const queryString = useMemo(() => {
    if (!surveyId) return '';
    const p = new URLSearchParams();
    p.set('survey_id', surveyId);
    if (range.from) p.set('date_from', range.from);
    if (range.to)   p.set('date_to', range.to);
    if (branchType && branchType !== 'all') p.set('branch_type', branchType);
    if (selectedBranchIds.length) p.set('branch_ids', selectedBranchIds.join(','));
    if (competitorBrand) p.set('competitor_brand', competitorBrand);
    return `?${p.toString()}`;
  }, [surveyId, range, branchType, selectedBranchIds, competitorBrand]);

  useEffect(() => {
    if (!surveyId) { setData(null); return; }
    let cancelled = false;
    (async () => {
      setDataLoading(true); setDataError(null);
      try {
        const res = await fetch(`${API}/experience-performance${queryString}`, { credentials: 'include' });
        if (!res.ok) {
          const err = await res.json().catch(() => ({}));
          if (!cancelled) setDataError(err.detail || `HTTP ${res.status}`);
          return;
        }
        const body = await res.json();
        if (!cancelled) setData(body);
      } catch (e) {
        if (!cancelled) setDataError('Failed to load Experience Performance');
      } finally {
        if (!cancelled) setDataLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [queryString, surveyId]);

  // ── Competitor brand list (mirrors MS Dashboard). ──
  const competitorBrands = useMemo(() => {
    const set = new Set();
    for (const b of (branches || [])) {
      if (b.branch_type !== 'competitor') continue;
      const brand = (b.competitor_brand || '').trim();
      if (brand) set.add(brand);
    }
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [branches]);

  useEffect(() => {
    if (competitorBrand && !competitorBrands.includes(competitorBrand)) {
      setCompetitorBrand('');
    }
  }, [competitorBrands, competitorBrand]);

  // ── Access-blocked (backend returned 403). ──
  if (surveysError && /mystery shopping/i.test(surveysError)) {
    return (
      <DashboardLayout>
        <div className="p-6">
          <EmptyState
            title="Mystery Shopping product not enabled"
            hint="Experience Performance is available to Mystery Shopping tenants."
          />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="p-6 space-y-4" data-testid="experience-performance-page">
        <div>
          <h1 className="text-2xl font-bold text-[#0F172A]">Experience Performance</h1>
          <p className="text-sm text-[#64748B] mt-1">
            See how consistently experience standards are delivered over time.
          </p>
        </div>

        {/* Survey selector — required for the calculation. */}
        <div className="bg-white rounded-xl border border-[#E2E8F0] p-4 flex flex-wrap items-center gap-3">
          <label htmlFor="ep-survey-select" className="text-xs font-semibold uppercase tracking-wider text-[#64748B]">
            Survey
          </label>
          <select
            id="ep-survey-select"
            value={surveyId}
            onChange={(e) => setSurveyId(e.target.value)}
            data-testid="ep-survey-select"
            disabled={surveysLoading || !surveys.length}
            className="h-9 rounded-md border border-[#E2E8F0] bg-white px-3 text-sm text-[#0F172A] focus:outline-none focus:ring-2 focus:ring-[#1A3E6F]/20 min-w-[240px]"
          >
            {surveysLoading && <option value="">Loading…</option>}
            {!surveysLoading && !surveys.length && <option value="">No standard MS surveys available</option>}
            {surveys.map((s) => (
              <option key={s.survey_id} value={s.survey_id}>{s.name}</option>
            ))}
          </select>
          {competitorBrands.length > 0 && (
            <div className="flex items-center gap-2 ml-auto" data-testid="ep-competitor-brand-filter">
              <label htmlFor="ep-competitor-brand-select" className="text-xs text-[#64748B]">
                Competitor Brand
              </label>
              <select
                id="ep-competitor-brand-select"
                value={competitorBrand}
                onChange={(e) => setCompetitorBrand(e.target.value)}
                data-testid="ep-competitor-brand-select"
                className="h-9 rounded-md border border-[#E2E8F0] bg-white px-2 text-xs text-[#0F172A] focus:outline-none focus:ring-2 focus:ring-[#1A3E6F]/20"
              >
                <option value="">All competitors</option>
                {competitorBrands.map((brand) => (
                  <option key={brand} value={brand}>{brand}</option>
                ))}
              </select>
            </div>
          )}
        </div>

        {/* Date + branch filters (reused from MS Dashboard). */}
        <FiltersBar
          range={range}
          onRangeChange={setRange}
          branchType={branchType}
          onBranchTypeChange={setBranchType}
          selectedBranchIds={selectedBranchIds}
          onSelectedBranchIdsChange={setSelectedBranchIds}
          branches={branches}
        />

        {/* Body */}
        {!surveyId && !surveysLoading && (
          <EmptyState title="Select a survey" hint="Sections, questions and answer options come from the selected survey configuration." />
        )}

        {dataLoading && (
          <div className="text-sm text-[#64748B] p-4" data-testid="ep-loading">Loading…</div>
        )}

        {dataError && !dataLoading && (
          <EmptyState title="Unable to load Experience Performance" hint={dataError} />
        )}

        {!dataLoading && !dataError && data && data.sections?.length === 0 && (
          <EmptyState
            title="No qualifying results for the current filters"
            hint="Try widening the date range, changing the branch or classification filter, or selecting a different survey."
          />
        )}

        {!dataLoading && !dataError && data && data.sections?.length > 0 && (
          <div className="space-y-6" data-testid="ep-sections">
            <p className="text-xs text-[#64748B]" data-testid="ep-reports-included">
              Based on {data.reports_included} published report{data.reports_included === 1 ? '' : 's'} for {data.survey?.name || 'the selected survey'}.
            </p>
            {data.sections.map((section) => {
              // Flatten groups → questions. Card layout no longer needs
              // per-option-set grouping; each card carries its own
              // options list. Backend already preserves group + question
              // order within a section, so a simple two-level flatten
              // keeps the configured order intact.
              const flatQuestions = [];
              for (const g of (section.groups || [])) {
                for (const q of (g.questions || [])) {
                  flatQuestions.push({ q, options: g.options });
                }
              }
              // Sections with no eligible questions are already dropped
              // by the backend; defensive guard for edge cases only.
              if (!flatQuestions.length) return null;
              return (
                <div key={section.section_id || section.name} data-testid={`ep-section-${section.section_id || 'ungrouped'}`}>
                  <h2 className="text-[11px] uppercase tracking-wider font-bold text-[#1A3E6F] mb-2">
                    {section.name}
                  </h2>
                  <div className="space-y-3">
                    {flatQuestions.map(({ q, options }) => (
                      <QuestionCard key={q.question_id} question={q} options={options} queryString={queryString} />
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
